import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import qs from 'qs';

import { LANDING_PATH, QUERY_PARAMS } from 'shared/consts';
import { BOOKINGS_PATH, getBookingDetailsPath } from 'shared/consts/paths';
import { BOOKINGS_TAB_PARAM } from 'store/bookings-tab/consts';
import { getTransactions, resetPatchTransactionStatus, resetRedirectTransaction } from 'store/transactions/actions';
import { resetCart, resetGetCartStatus, resetSubmitCartStatus } from 'store/cart/actions';
import { push, replace } from 'store/router/actions';
import { updateInitialRoute } from 'store/routes/actions';
import { pick } from 'utils/pickObjectProperty';
import { useIsPaneLayout } from 'hooks/use-is-pane-layout.hook';
import { useRedirectHostToBooking } from 'hooks/use-redirect-to-bookings.hook';

/* The upcoming tab of My bookings, where a landing with no booking to select goes. */
const UPCOMING_TAB = 'upcoming';

/*
 * The query the landing page keeps, taken from the booking flow's. The time selection is dropped:
 * the booking was made, and carrying its slot back to the landing page would pre-fill a search for
 * a time the tenant has just taken. Same filter the old receipt's Done applied (`useReceiptScreen`).
 */
const DROPPED_ON_LANDING = new Set([
  'startTime',
  'startDate',
  'endTime',
  'isExact',
  'duration',
  'presetWindows',
  'startDates',
  'capacity',
]);

export const landingQueryString = (params: Record<string, unknown> | undefined): string =>
  qs.stringify(
    pick(
      params ?? {},
      QUERY_PARAMS.filter(param => !DROPPED_ON_LANDING.has(param)),
    ),
  );

export interface MyBookingsTarget {
  /* The booking to select. */
  transactionUuid?: string;
  /*
   * Its `details.id`, which the booking's own page is addressed by.
   *
   * A multi-day purchase passes its FIRST booking: helix's cart submit returns the first item's
   * transaction (`const [result] = await Promise.all(items.map(...))`), so that is the uuid the cart
   * carries and the transaction the checkout fetched. The other days are listed beside it.
   */
  bookingId?: number | string;
  /* The app's own context params (`appBrand`, `buildingUuid`, …), carried onto every path. */
  query?: Record<string, unknown>;
}

/**
 * RES-1407 — My bookings with one booking selected, as the paths to push, in order.
 *
 * ⛔ TWO SHAPES, AND THE LAYOUT PICKS. In the portal's columns the list selects the booking in
 * place from `?transactionUuid=` (`useRestoreSelectedBooking`). Everywhere else the list would
 * answer that same param by pushing the booking's page on top of itself, and then answer it AGAIN
 * every time back returns to the list: the restore re-arms on remount, so back would bounce
 * straight into the booking. So outside the panes the list is pushed bare and the booking's page
 * is pushed on top of it, which is the history a tap on the tile would have made.
 */
export const myBookingsPaths = (
  { transactionUuid, bookingId, query = {} }: MyBookingsTarget,
  isPaneLayout: boolean,
): string[] => {
  const withQuery = (path: string, extra: Record<string, unknown> = {}): string => {
    const search = qs.stringify({ ...query, ...extra });

    return search ? `${path}?${search}` : path;
  };
  const hasBookingId = bookingId !== undefined && bookingId !== null;

  if (isPaneLayout && transactionUuid) {
    return [withQuery(BOOKINGS_PATH, { transactionUuid })];
  }

  if (!isPaneLayout && hasBookingId) {
    return [withQuery(BOOKINGS_PATH), withQuery(getBookingDetailsPath(bookingId))];
  }

  /*
   * Nothing this layout can select with: no ids at all, or only the one the OTHER layout uses. Outside
   * the panes a bare uuid would be the `?transactionUuid=` bounce described above; inside them a bare
   * id has no param to select by. The upcoming tab lists the booking either way.
   */
  return [withQuery(BOOKINGS_PATH, { [BOOKINGS_TAB_PARAM]: UPCOMING_TAB })];
};

/** RES-1407 — push My bookings with one booking selected (see `myBookingsPaths`). */
export const useOpenMyBookings = (): ((target: MyBookingsTarget) => void) => {
  const dispatch = useDispatch();
  const isPaneLayout = useIsPaneLayout();

  return useCallback(
    (target: MyBookingsTarget) => {
      myBookingsPaths(target, isPaneLayout).forEach(path => dispatch(push(path)));
    },
    [dispatch, isPaneLayout],
  );
};

interface LandOnBookingInput extends MyBookingsTarget {
  /* The booking flow's query, for the landing page underneath. */
  params: Record<string, unknown> | undefined;
}

/**
 * RES-1407 — what follows a booking now that the old full receipt is gone, under the 2-way chat
 * redesign: My bookings, with the booking just made selected. The post-checkout add-on offer
 * (Booking confirmed → Browse add-ons) is untouched; this is where its ways out lead, and where a
 * booking with no offer goes straight away.
 *
 * ⛔ THE LANDING PAGE REPLACES THE BOOKING FLOW FIRST. On native, the header's back is the host's own
 * history pop, which this app cannot intercept, so whatever sits under My bookings is where back
 * goes. Without the replace, that is the resource and time page the tenant just booked from.
 *
 * The finished checkout's state is cleared here, which the old receipt's Done used to do on its way
 * out. `submitCartStatus` left FULFILLED is what every checkout surface watches to navigate after a
 * purchase, so the next checkout that mounts would replay this one's navigation. The transaction is
 * kept: the lists are refetched, and the add-on step prefers the store's copy over a refetch.
 */
export const usePostBookingLanding = (): ((input: LandOnBookingInput) => void) => {
  const dispatch = useDispatch();
  const openMyBookings = useOpenMyBookings();
  const redirectHostToBooking = useRedirectHostToBooking();

  return useCallback(
    ({ params, ...target }: LandOnBookingInput) => {
      dispatch(resetSubmitCartStatus());
      dispatch(resetGetCartStatus());
      dispatch(resetPatchTransactionStatus());
      dispatch(resetRedirectTransaction());
      dispatch(resetCart());
      dispatch(getTransactions.request({}));

      const landingQuery = landingQueryString(params);

      dispatch(updateInitialRoute(LANDING_PATH));
      dispatch(replace(`${LANDING_PATH}${landingQuery ? `?${landingQuery}` : ''}`));
      openMyBookings({ ...target, query: qs.parse(landingQuery) });
      // Moving only this frame leaves the portal's url, breadcrumb and back on the resource page.
      redirectHostToBooking(target.transactionUuid);
    },
    [dispatch, openMyBookings, redirectHostToBooking],
  );
};
